// Pieces the administration pages share: rights attribution as chips (06-modules §1.5), the
// account state pill, the identity cell, the approve/reinstate dialog (structurally requires ≥1
// group — permission-model 05-administration §5) and the in-place failure notice that renders a
// 403 as permission-denied rather than as a button that silently failed (06-modules §1.7).
import { useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import type { AdminGroupRow, AdminGroupsResponse, AdminUserRow } from '../../shared/api-types.js';
import type { SubjectStatus } from '../../shared/enums.js';
import { failureKind, type ApiFailure } from '../api.js';
import {
  Avatar,
  Banner,
  Button,
  Checkbox,
  Chip,
  CopyButton,
  Dialog,
  FailureView,
  Fieldset,
  Skeleton,
  StatusPill,
  pluralise,
  useRequest,
} from '../ui/index.js';
import { PATHS } from '../paths.js';
import { adminApi } from './admin-api.js';
import { ACCOUNT_DELETION_WORDS, STATE_TONES, STATE_WORDS, approvalBlocker, describeConfers, describeRights } from './admin-helpers.js';

// --- Rights -------------------------------------------------------------------------------------

export interface RightsChipsProps {
  rights: unknown;
  /** Link each group chip to its Groups page — only when the reader holds that section. */
  linkGroups?: boolean;
  /** Accessible context for the "no rights" answer, e.g. the person's name. */
  who?: string;
}

/**
 * One chip per conferring source, wrapping within the cell and never truncating the set. The
 * superuser marker is a different kind of fact from a granted role and is marked distinctly; so is
 * an administrative-section grant (06-modules §1.5 rule 2, D8) — a key, its reach in its text, and
 * its kind named to assistive technology, never a link: it is conferred by the group chip beside it.
 * An account with no sources answers with the dash; rights that could not be read say so instead.
 */
export function RightsChips({ rights, linkGroups = false, who }: RightsChipsProps) {
  const chips = describeRights(rights);
  if (chips === null) {
    return <span className="rights-chips__failed">Could not load</span>;
  }
  if (chips.length === 0) {
    return (
      <span className="rights-chips__none">
        <span aria-hidden="true">—</span>
        <span className="visually-hidden">{who ? `${who} has no rights` : 'No rights'}</span>
      </span>
    );
  }
  return (
    <ul className="rights-chips" role="list" aria-label="Rights and their sources">
      {chips.map((chip) => {
        if (chip.kind === 'superuser') {
          return (
            <li key={chip.key}>
              <Chip tone="accent" icon="shield" title="Superuser, set by the deployment's configuration — not a group grant">
                {chip.label}
              </Chip>
            </li>
          );
        }
        if (chip.kind === 'group') {
          return (
            <li key={chip.key}>
              <Chip icon="users" to={linkGroups && chip.groupId ? PATHS.adminGroup(chip.groupId) : undefined} title={`Group: ${chip.label}`}>
                {chip.label}
              </Chip>
            </li>
          );
        }
        if (chip.kind === 'section') {
          return (
            <li key={chip.key}>
              <Chip icon="key" className="rights-chips__section" title={`Administrative section: ${chip.label} — ${chip.verbs ?? 'view'}, conferred by a group`}>
                <span className="visually-hidden">Administrative section: </span>
                {chip.label}
              </Chip>
            </li>
          );
        }
        return (
          <li key={chip.key}>
            <Chip icon="bolt" title={`Capability: ${chip.label}`}>{chip.label}</Chip>
          </li>
        );
      })}
    </ul>
  );
}

// --- State --------------------------------------------------------------------------------------

export function StatePill({ status }: { status: SubjectStatus }) {
  return (
    <StatusPill tone={STATE_TONES[status]}>
      {STATE_WORDS[status]}
    </StatusPill>
  );
}

// --- Identity -----------------------------------------------------------------------------------

/**
 * The body of an account's deletion confirmation, on the roster and on the account page alike (03-components §4.2; the
 * re-audit's R22 and the owner's erasure ruling of 2026-10-10 — `ACCOUNT_DELETION_WORDS`): what the account holds, what
 * colleagues lose, what of the person stays in colleagues' own records, and that it cannot be undone.
 */
export function AccountDeletionBody() {
  return (
    <>
      <p>{ACCOUNT_DELETION_WORDS.theirs}</p>
      <p>{ACCOUNT_DELETION_WORDS.colleagues}</p>
      <p>{ACCOUNT_DELETION_WORDS.records}</p>
      <p>{ACCOUNT_DELETION_WORDS.closing}</p>
    </>
  );
}

export interface IdentityCellProps {
  row: Pick<AdminUserRow, 'id' | 'displayName' | 'email'> & { initials?: string };
  /** Router path to open the account, when the cell should be a link. */
  to?: string;
  /** A short note beside the name, e.g. "You". */
  aside?: ReactNode;
}

/** Both a human name and the unique address (06-modules §1.4), with the avatar as a decorative repeat. */
export function IdentityCell({ row, to, aside }: IdentityCellProps) {
  const name = to ? <Link to={to} className="identity-cell__name hit-area">{row.displayName}</Link> : <span className="identity-cell__name">{row.displayName}</span>;
  return (
    <span className="identity-cell">
      <Avatar name={row.displayName} email={row.email} hashKey={row.id} initials={row.initials} size="sm" decorative />
      <span className="identity-cell__text">
        <span className="identity-cell__line">
          {name}
          {aside ? <span className="identity-cell__aside">{aside}</span> : null}
        </span>
        <span className="identity-cell__email">{row.email}</span>
      </span>
    </span>
  );
}

// --- Operation failure --------------------------------------------------------------------------

export interface OperationFailureProps {
  failure: ApiFailure | null;
  /** What was attempted, in the user's words: "suspend this account". */
  attempted: string;
  onDismiss?: () => void;
}

/**
 * A mutation that failed, stated where the control lives. A 403 means the server re-resolved the
 * caller's permission and refused: the message says access is restricted (never "try again"),
 * because a retry would fail identically.
 */
export function OperationFailure({ failure, attempted, onDismiss }: OperationFailureProps) {
  if (!failure) return null;
  const kind = failureKind(failure);
  if (kind === 'denied') {
    return (
      <Banner tone="danger" title="Access restricted" onDismiss={onDismiss}>
        <p>Calis refused to {attempted}: your account does not include this right now. The page has been refreshed to match.</p>
      </Banner>
    );
  }
  const title = kind === 'not-found' ? 'Not found' : kind === 'unavailable' ? 'Temporarily unavailable' : `We couldn't ${attempted}`;
  return (
    <Banner tone="danger" title={title} onDismiss={onDismiss}>
      <p>{failure.message}</p>
      {failure.correlationId ? (
        <p className="text-xs muted">
          Reference <code className="selectable">{failure.correlationId}</code> <CopyButton value={failure.correlationId} label="Copy reference" />
        </p>
      ) : null}
    </Banner>
  );
}

// --- Approve / reinstate dialog ---------------------------------------------------------------

export interface ApproveDialogProps {
  open: boolean;
  /** The account(s) being approved; more than one is a bulk approval and the confirmation names the count. */
  accounts: readonly Pick<AdminUserRow, 'id' | 'displayName' | 'email' | 'status'>[];
  mode: 'approve' | 'reinstate';
  onCancel: () => void;
  /** Runs the approval(s) with the chosen groups; resolves when done (the caller reports outcomes). */
  onConfirm: (groupIds: string[]) => Promise<void>;
  busy?: boolean;
  /** Live progress text for a bulk run ("Approving 2 of 5 — Ada Lovelace"). */
  progress?: string | null;
  /** Per-account failures after a bulk run, kept in the dialog so nothing is lost when it closes. */
  failures?: readonly { name: string; message: string }[];
  error?: ApiFailure | null;
}

/**
 * Approval cannot complete without at least one group (structural, not merely expected): the
 * confirm button stays disabled with the reason stated until one is chosen, and until at least one
 * chosen group actually gives access to Calis — otherwise the approval would grant nothing usable.
 */
export function ApproveDialog({ open, accounts, mode, onCancel, onConfirm, busy = false, progress = null, failures = [], error = null }: ApproveDialogProps) {
  // Why: groups are loaded when the dialog mounts, not with the page — a delegate never opens this
  // dialog, so the superuser-only request is never sent on their behalf.
  const groups = useRequest<AdminGroupsResponse>((signal) => adminApi.groups(signal), []);
  const [chosen, setChosen] = useState<string[]>([]);
  const rows: AdminGroupRow[] = useMemo(() => groups.data?.items ?? [], [groups.data]);
  const blocker = approvalBlocker(chosen, rows);
  const bulk = accounts.length > 1;
  const first = accounts[0];
  const verb = mode === 'reinstate' ? 'Reinstate' : 'Approve';
  const title = bulk ? `${verb} ${accounts.length} accounts?` : first ? `${verb} ${first.displayName}?` : `${verb} account`;
  const confirmLabel = bulk ? `${verb} ${accounts.length} accounts` : verb;

  const toggle = (id: string, checked: boolean): void => {
    setChosen((current) => (checked ? [...new Set([...current, id])] : current.filter((value) => value !== id)));
  };

  return (
    <Dialog
      open={open}
      onClose={() => { if (!busy) onCancel(); }}
      title={title}
      description={
        bulk
          ? 'Every account below joins the groups you choose.'
          : mode === 'reinstate'
            ? 'Choose the groups again — earlier memberships were removed when the account was suspended or denied.'
            : 'Choose the groups that give this account its access.'
      }
      size="md"
      initialFocus="first"
      unsavedChanges={chosen.length > 0 && !busy}
      actions={
        <>
          <Button variant="secondary" onClick={onCancel} disabled={busy}>{failures.length > 0 ? 'Close' : 'Cancel'}</Button>
          <span className="approve-dialog__confirm" title={blocker ?? undefined}>
            <Button variant="primary" onClick={() => void onConfirm(chosen)} disabled={blocker !== null || busy} loading={busy}>{confirmLabel}</Button>
          </span>
        </>
      }
    >
      <div className="stack stack--3">
        {bulk ? (
          <ul className="approve-dialog__accounts" aria-label="Accounts to approve">
            {accounts.map((account) => (
              <li key={account.id}>
                <span>{account.displayName}</span> <span className="muted text-sm">{account.email}</span>
              </li>
            ))}
          </ul>
        ) : first ? (
          <p className="secondary text-sm">{first.email}</p>
        ) : null}

        <Fieldset legend="Groups">
          {groups.status === 'loading' ? (
            <div className="stack stack--2" aria-busy="true">
              <Skeleton width="60%" />
              <Skeleton width="50%" />
              <Skeleton width="55%" />
            </div>
          ) : groups.status === 'error' && groups.error ? (
            <FailureView failure={groups.error} what="the groups" onRetry={groups.reload} compact />
          ) : rows.length === 0 ? (
            <p className="secondary text-sm">No groups exist yet — create one on the Groups page first.</p>
          ) : (
            rows.map((group) => {
              const confers = describeConfers(group.confers, groups.data?.actDelegableSections ?? []);
              return (
                <Checkbox
                  key={group.id}
                  label={group.name}
                  description={confers.length > 0 ? confers.join(' · ') : 'Grants nothing yet'}
                  checked={chosen.includes(group.id)}
                  onChange={(event) => toggle(group.id, event.target.checked)}
                  disabled={busy}
                />
              );
            })
          )}
        </Fieldset>

        <p className="text-sm" role="status">
          {blocker ? <span className="approve-dialog__blocker">{blocker}</span> : <span className="secondary">Takes effect on the person's next request.</span>}
        </p>

        {progress ? <p className="admin-progress" role="status" aria-live="polite">{progress}</p> : null}
        {failures.length > 0 ? (
          <Banner tone="danger" title={pluralise(failures.length, { one: '# account was not approved', other: '# accounts were not approved' })}>
            <ul className="approve-dialog__failures">
              {/* Each step's line was made by `inlineFailureText` where it failed (AdminUsersPage; ruling Q11). */}
              {failures.map((step) => (
                <li key={step.name}><strong>{step.name}</strong>: {step.message}</li>
              ))}
            </ul>
          </Banner>
        ) : null}
        {error ? <OperationFailure failure={error} attempted={mode === 'reinstate' ? 'reinstate this account' : 'approve this account'} /> : null}
      </div>
    </Dialog>
  );
}
